import { useState, useEffect, ChangeEvent, FormEvent } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Avatar,
  Alert,
  CircularProgress,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import type { UpdateUserData, User } from '@/types'
import { usersApi } from '@/api/users'

interface ProfileEditDialogProps {
  open: boolean
  user: User | null | undefined
  onClose: () => void
  onSaved?: (updated: User) => void
}

export function ProfileEditDialog({ open, user, onClose, onSaved }: ProfileEditDialogProps) {
  const { t } = useTranslation()

  const [firstName, setFirstName] = useState('')
  const [surname, setSurname] = useState('')
  const [middleName, setMiddleName] = useState('')
  const [birthDate, setBirthDate] = useState('')
  const [avatarFile, setAvatarFile] = useState<File | null>(null)
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open && user) {
      setFirstName(user.first_name ?? '')
      setSurname(user.surname ?? '')
      setMiddleName(user.middle_name ?? '')
      setBirthDate(user.birthday ?? '')
      setAvatarFile(null)
      setAvatarPreview(user.avatar_url ?? null)
      setError(null)
    }
    if (!open) {
      setAvatarFile(null)
    }
  }, [open, user])

  const handleAvatarChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    if (!file.type.startsWith('image/')) {
      setError(t('profile.avatarInvalidType'))
      return
    }
    setError(null)
    setAvatarFile(file)
    const reader = new FileReader()
    reader.onload = () => {
      setAvatarPreview(reader.result as string)
    }
    reader.readAsDataURL(file)
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (!user) return
    setSubmitting(true)
    setError(null)
    try {
      const payload: UpdateUserData = {
        surname,
        first_name: firstName,
        middle_name: middleName,
        birthday: birthDate || undefined,
      }

      const updated = await usersApi.update(user.id, payload, avatarFile || undefined)
      onSaved?.(updated)
      onClose()
    } catch (e) {
      const err = e as { message?: string }
      setError(err.message || t('profile.updateError'))
    } finally {
      setSubmitting(false)
    }
  }

  const getInitials = () => {
    const parts = [surname, firstName].filter(Boolean)
    if (parts.length) {
      return parts
        .join(' ')
        .split(' ')
        .filter(Boolean)
        .map((p) => p[0]?.toUpperCase())
        .join('')
        .slice(0, 2)
    }
    if (user?.email) {
      return user.email[0]?.toUpperCase()
    }
    return '?'
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>{t('profile.edit')}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1, mb: 3 }}>
            <Avatar
              src={avatarPreview || undefined}
              sx={{ width: 64, height: 64, fontSize: '1.5rem' }}
            >
              {getInitials()}
            </Avatar>
            <Box>
              <Button
                variant="outlined"
                component="label"
                size="small"
                sx={{ textTransform: 'none' }}
              >
                {t('profile.changeAvatar')}
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleAvatarChange}
                />
              </Button>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label={t('profile.surname')}
              fullWidth
              size="small"
              value={surname}
              onChange={(e) => setSurname(e.target.value)}
            />
            <TextField
              label={t('profile.firstName')}
              fullWidth
              size="small"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
            <TextField
              label={t('profile.middleName')}
              fullWidth
              size="small"
              value={middleName}
              onChange={(e) => setMiddleName(e.target.value)}
            />
            <TextField
              label={t('profile.birthDate')}
              type="date"
              fullWidth
              size="small"
              value={birthDate || ''}
              onChange={(e) => setBirthDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={20} /> : null}
          >
            {t('common.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

